import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, RefreshCw, CheckCircle2, XCircle, AlertTriangle, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { qcAPI } from '../../services/api';

const emptyResult = (item) => ({
  lab_test_id: item.lab_test_id,
  result_value: '',
  corrective_action: '',
  westgard_status: '',
  westgard_rules: [],
});

function evaluate(item, resultValue, rejectionMultiplier = 3) {
  if (resultValue === '' || Number.isNaN(Number(resultValue))) return { status: '', min: null, max: null };
  const mean = Number(item.target_mean || 0);
  const sd = Number(item.target_sd || 0);
  const min = mean - sd * rejectionMultiplier;
  const max = mean + sd * rejectionMultiplier;
  const value = Number(resultValue);
  const status = value >= min && value <= max ? 'pass' : 'fail';
  return { status, min, max };
}

function QCDailyEntry() {
  const [loading, setLoading] = useState(true);
  const [loadingRun, setLoadingRun] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [controls, setControls] = useState([]);
  const [selectedControlId, setSelectedControlId] = useState('');
  const [results, setResults] = useState([]);
  const [runDate, setRunDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');

  const loadControls = async () => {
    setLoading(true);
    try {
      const response = await qcAPI.getActiveControls();
      const items = response.data?.data || [];
      setControls(Array.isArray(items) ? items : []);
      if (Array.isArray(items) && items.length && !selectedControlId) {
        setSelectedControlId(items[0]._id);
      }
    } catch (error) {
      toast.error('تعذر تحميل الكنترولات النشطة');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadControls();
  }, []);

  const selectedControl = useMemo(
    () => controls.find((c) => c._id === selectedControlId) || null,
    [controls, selectedControlId]
  );

  const loadRunForDate = async (control, dateValue) => {
    if (!control?._id || !dateValue) {
      setResults([]);
      setNotes('');
      return;
    }

    setLoadingRun(true);
    try {
      const response = await qcAPI.getRuns({
        control_id: control._id,
        from: dateValue,
        to: dateValue,
      });
      const runs = response.data?.data || [];
      const normalizedDate = new Date(dateValue).toISOString().slice(0, 10);
      const todayRun = runs.find(
        (run) => new Date(run.run_date).toISOString().slice(0, 10) === normalizedDate
      );

      const entryMap = new Map(
        (todayRun?.entries || []).map((entry) => [String(entry.lab_test_id), entry])
      );

      const nextResults = (control.items || []).map((item) => {
        const existing = entryMap.get(String(item.lab_test_id));
        return {
          lab_test_id: item.lab_test_id,
          result_value: existing?.result_value ?? '',
          corrective_action: existing?.corrective_action || '',
          westgard_status: existing?.westgard_status || '',
          westgard_rules: existing?.westgard_rules || [],
        };
      });

      setResults(nextResults);
      setNotes(todayRun?.notes || '');
    } catch (error) {
      setResults((control.items || []).map((item) => emptyResult(item)));
      setNotes('');
      toast.error('تعذر تحميل إدخالات التاريخ المحدد');
    } finally {
      setLoadingRun(false);
    }
  };

  useEffect(() => {
    if (!selectedControl) {
      setResults([]);
      setNotes('');
      return;
    }
    loadRunForDate(selectedControl, runDate);
  }, [selectedControl, runDate]);

  const updateResult = (index, patch) =>
    setResults((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], ...patch };
      return copy;
    });

  const rejectionMultiplier = useMemo(
    () => (selectedControl?.critical_rejection_threshold === '2sd' ? 2 : 3),
    [selectedControl]
  );

  const rows = useMemo(() => {
    if (!selectedControl) return [];
    return (selectedControl.items || []).map((item, index) => {
      const current = results[index] || emptyResult(item);
      const evalRes = evaluate(item, current.result_value, rejectionMultiplier);
      return {
        item,
        current,
        ...evalRes,
      };
    });
  }, [selectedControl, results, rejectionMultiplier]);

  const onSubmit = async () => {
    if (!selectedControlId) {
      toast.error('اختر الكنترول أولاً');
      return;
    }

    if (!rows.length) {
      toast.error('لا توجد تحاليل مرتبطة بهذا الكنترول');
      return;
    }

    const enteredRows = rows.filter(
      (row) => row.current.result_value !== '' && !Number.isNaN(Number(row.current.result_value))
    );
    if (!enteredRows.length) {
      toast.error('أدخل نتيجة واحدة على الأقل قبل الاعتماد');
      return;
    }

    for (let i = 0; i < enteredRows.length; i += 1) {
      const row = enteredRows[i];
      if (row.status === 'fail' && !String(row.current.corrective_action || '').trim()) {
        toast.error(`الإجراء التصحيحي مطلوب للتحليل ${row.item.test_name}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await qcAPI.submitRun({
        control_id: selectedControlId,
        run_date: runDate,
        notes,
        results: enteredRows.map((row) => ({
          lab_test_id: row.item.lab_test_id,
          result_value: Number(row.current.result_value),
          corrective_action: row.current.corrective_action || '',
        })),
      });

      toast.success('تم اعتماد نتائج الجودة بنجاح');
      await loadRunForDate(selectedControl, runDate);
    } catch (error) {
      toast.error(error.response?.data?.message || 'تعذر حفظ النتائج');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
                <ClipboardCheck className="w-6 h-6 text-slate-100" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1E293B]">إدخال نتائج الجودة اليومية</h1>
                <p className="text-sm text-slate-500 mt-1">إدخال قراءات اليوم مع تحقق لحظي من حدود القبول</p>
              </div>
            </div>
            <button
              onClick={loadControls}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              تحديث الكنترولات
            </button>
          </div>
        </motion.div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">اختر الكنترول</label>
              <select
                value={selectedControlId}
                onChange={(e) => setSelectedControlId(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white"
              >
                <option value="">اختر...</option>
                {controls.map((control) => (
                  <option key={control._id} value={control._id}>
                    {control.control_name} - {control.lot_number}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">تاريخ التشغيل</label>
              <input
                type="date"
                value={runDate}
                onChange={(e) => setRunDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-700">التحاليل المرتبطة بالكنترول</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="border-b border-slate-200">
                <tr className="text-right text-xs text-slate-500">
                  <th className="px-4 py-3">التحليل</th>
                  <th className="px-4 py-3">النتيجة</th>
                  <th className="px-4 py-3">الحالة</th>
                  <th className="px-4 py-3">Westgard</th>
                  <th className="px-4 py-3">الإجراء التصحيحي</th>
                </tr>
              </thead>
              <tbody>
                {!selectedControl ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-slate-500">
                      اختر كنترول نشط لعرض التحاليل
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-slate-500">
                      لا توجد تحاليل مرتبطة بهذا الكنترول
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.item.lab_test_id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{row.item.test_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          المجال المقبول (±{rejectionMultiplier}SD): {(row.item.target_mean - row.item.target_sd * rejectionMultiplier).toFixed(1)} -{' '}
                          {(row.item.target_mean + row.item.target_sd * rejectionMultiplier).toFixed(1)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="any"
                          value={row.current.result_value}
                          onChange={(e) => updateResult(index, { result_value: e.target.value })}
                          onBlur={(e) => updateResult(index, { result_value: e.target.value })}
                          className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none ${
                            row.status === 'pass'
                              ? 'border-emerald-300 bg-emerald-50'
                              : row.status === 'fail'
                              ? 'border-rose-300 bg-rose-50'
                              : 'border-slate-200'
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {row.status === 'pass' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Pass
                          </span>
                        ) : row.status === 'fail' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-xs">
                            <XCircle className="w-3.5 h-3.5" /> Fail
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.current.westgard_status === 'rejected' ? (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-100 text-rose-700 text-xs">
                              <ShieldAlert className="w-3 h-3" /> Rejected
                            </span>
                            {row.current.westgard_rules?.length > 0 && (
                              <p className="text-[10px] text-rose-600 mt-1">{row.current.westgard_rules.join(', ')}</p>
                            )}
                          </div>
                        ) : row.current.westgard_status === 'warning' ? (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs">
                              <AlertTriangle className="w-3 h-3" /> Warning
                            </span>
                            {row.current.westgard_rules?.length > 0 && (
                              <p className="text-[10px] text-amber-600 mt-1">{row.current.westgard_rules.join(', ')}</p>
                            )}
                          </div>
                        ) : row.current.westgard_status === 'accepted' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs">
                            <CheckCircle2 className="w-3 h-3" /> OK
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.status === 'fail' ? (
                          <textarea
                            rows={2}
                            value={row.current.corrective_action}
                            onChange={(e) => updateResult(index, { corrective_action: e.target.value })}
                            className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm"
                            placeholder="اذكر الإجراء التصحيحي"
                          />
                        ) : (
                          <span className="text-slate-400 text-xs">غير مطلوب</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          {loadingRun && (
            <p className="text-xs text-slate-500 mb-2">جاري تحميل إدخالات التاريخ المحدد...</p>
          )}
          <label className="block text-sm font-medium text-slate-700 mb-1.5">ملاحظات (اختياري)</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl mb-4"
          />
          <button
            onClick={onSubmit}
            disabled={submitting || !selectedControlId}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-60"
          >
            <ClipboardCheck className="w-4 h-4" />
            {submitting ? 'جاري الاعتماد...' : 'اعتماد النتائج'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QCDailyEntry;
